/** `sections/showcase-slideshow.liquid` — `<showcase-slideshow>`: accordion-style
 *  slideshow on desktop; horizontal swipe/scroll carousel on mobile (≤768px).
 *
 *  A bottom-centered pagination pill reflects the active slide: inactive slides
 *  are cream dots, the active slide is an elongated dark pill. Autoplay advances
 *  on a timer and pauses on hover/focus, when the tab is hidden, and when reduced
 *  motion is preferred. Clicking an indicator jumps to that slide. */

const SLIDE_SELECTOR = '[data-slideshow-slide]'
const TRACK_SELECTOR = '[data-slideshow-track]'
const ITEM_SELECTOR = '[data-slideshow-pagination-item]'
const ACTIVE_CLASS = 'showcase-slideshow-slide-active'
const ITEM_ACTIVE_CLASS = 'is-active'
const ITEM_FILLING_CLASS = 'is-filling'
const PAUSED_CLASS = 'is-paused'
const DESKTOP_QUERY = '(min-width: 769px)'
const REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const AUTOPLAY_MS = 5000

class ShowcaseSlideshow extends HTMLElement {
  #abort
  #accordionAbort
  #mql
  #reduceMotionMql
  #track
  #slides = []
  #items = []
  #index = 0
  #autoplayMs = AUTOPLAY_MS
  #autoplayTimer
  #autoplayStart = 0
  #autoplayRemaining = AUTOPLAY_MS
  #paused = false
  #scrollSyncing = false
  #scrollTimer
  #scrollSyncTimer

  connectedCallback() {
    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.#track = this.querySelector(TRACK_SELECTOR)
    this.#slides = [...this.querySelectorAll(SLIDE_SELECTOR)]
    this.#items = [...this.querySelectorAll(ITEM_SELECTOR)]
    this.#index = Math.max(
      0,
      this.#slides.findIndex((slide) => slide.classList.contains(ACTIVE_CLASS))
    )

    // Autoplay delay (ms) is set per section via `data-slideshow-timeout`; fall
    // back to the default when the attribute is missing or invalid.
    this.#autoplayMs =
      Number.parseInt(this.dataset.slideshowTimeout ?? '', 10) || AUTOPLAY_MS
    this.#autoplayRemaining = this.#autoplayMs
    this.style.setProperty('--slideshow-autoplay-ms', `${this.#autoplayMs}ms`)

    this.#mql = globalThis.matchMedia(DESKTOP_QUERY)
    this.#reduceMotionMql = globalThis.matchMedia(REDUCE_MOTION_QUERY)
    this.#mql.addEventListener('change', this.#onBreakpointChange, { signal })

    this.#items.forEach((item, i) => {
      item.addEventListener(
        'click',
        () => this.#goTo(i, { animate: true, user: true }),
        { signal }
      )
    })

    this.addEventListener('pointerenter', this.#pause, { signal })
    this.addEventListener('pointerleave', this.#resume, { signal })
    this.addEventListener('focusin', this.#pause, { signal })
    this.addEventListener('focusout', this.#resume, { signal })
    document.addEventListener('visibilitychange', this.#onVisibility, { signal })

    if (this.#track) {
      this.#track.addEventListener('scroll', this.#onScroll, {
        passive: true,
        signal,
      })
    }

    this.#applyMode()
    this.#startTimer()
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#teardownAccordion()
    clearTimeout(this.#autoplayTimer)
    clearTimeout(this.#scrollTimer)
    clearTimeout(this.#scrollSyncTimer)
  }

  get #canAutoplay() {
    return this.#slides.length > 1 && !this.#reduceMotionMql.matches
  }

  #onBreakpointChange = () => {
    this.#applyMode()
    this.#goTo(this.#index, { animate: false })
  }

  #applyMode() {
    if (this.#mql.matches) {
      this.#setupAccordion()
    } else {
      this.#teardownAccordion()
    }
  }

  #setupAccordion() {
    if (this.#accordionAbort) return
    this.#accordionAbort = new AbortController()
    const { signal } = this.#accordionAbort

    this.#refreshAccordionRoles()
    this.addEventListener('click', this.#onSlideClick, { signal })
    this.addEventListener('keydown', this.#onSlideKeydown, { signal })
    this.addEventListener('pointerover', this.#onSlideHover, { signal })
  }

  #teardownAccordion() {
    this.#accordionAbort?.abort()
    this.#accordionAbort = undefined

    this.#slides.forEach((slide) => {
      slide.removeAttribute('tabindex')
      slide.removeAttribute('role')
    })
  }

  #refreshAccordionRoles() {
    if (!this.#accordionAbort) return
    this.#slides.forEach((slide) => {
      if (slide.classList.contains(ACTIVE_CLASS)) {
        slide.removeAttribute('tabindex')
        slide.removeAttribute('role')
      } else {
        slide.setAttribute('tabindex', '0')
        slide.setAttribute('role', 'button')
      }
    })
  }

  #onSlideClick = (event) => {
    if (event.target.closest(ITEM_SELECTOR)) return
    const slide = event.target.closest(SLIDE_SELECTOR)
    if (!slide || slide.classList.contains(ACTIVE_CLASS)) return
    const index = this.#slides.indexOf(slide)
    if (index >= 0) this.#goTo(index, { animate: false, user: true })
  }

  #onSlideHover = (event) => {
    // Mouse only — on touch the tap/click handler already activates the slide.
    if (event.pointerType !== 'mouse') return
    if (event.target.closest(ITEM_SELECTOR)) return
    const slide = event.target.closest(SLIDE_SELECTOR)
    if (!slide || slide.classList.contains(ACTIVE_CLASS)) return
    const index = this.#slides.indexOf(slide)
    if (index >= 0) this.#goTo(index, { user: true })
  }

  #onSlideKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    const slide = event.target.closest(SLIDE_SELECTOR)
    if (!slide || slide.classList.contains(ACTIVE_CLASS)) return
    event.preventDefault()
    const index = this.#slides.indexOf(slide)
    if (index >= 0) this.#goTo(index, { animate: false, user: true })
  }

  #onScroll = () => {
    if (this.#mql.matches || this.#scrollSyncing) return
    clearTimeout(this.#scrollTimer)
    this.#scrollTimer = setTimeout(() => {
      const index = this.#nearestSlide()
      if (index !== this.#index) this.#goTo(index, { fromScroll: true })
    }, 100)
  }

  #nearestSlide() {
    if (!this.#track) return this.#index
    const trackRect = this.#track.getBoundingClientRect()
    const trackCenter = trackRect.left + trackRect.width / 2
    let best = 0
    let min = Infinity
    this.#slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect()
      const distance = Math.abs(rect.left + rect.width / 2 - trackCenter)
      if (distance < min) {
        min = distance
        best = i
      }
    })
    return best
  }

  #goTo(index, { animate = false, fromScroll = false } = {}) {
    const count = this.#slides.length
    if (count === 0) return

    const next = ((index % count) + count) % count
    this.#index = next

    this.#slides.forEach((slide, i) => {
      slide.classList.toggle(ACTIVE_CLASS, i === next)
    })
    this.#items.forEach((item, i) => {
      const isActive = i === next
      item.classList.toggle(ITEM_ACTIVE_CLASS, isActive)
      if (isActive) {
        item.setAttribute('aria-current', 'true')
      } else {
        item.removeAttribute('aria-current')
      }
    })

    this.#refreshAccordionRoles()

    if (!this.#mql.matches && !fromScroll && this.#track) {
      this.#scrollSyncing = true
      const trackRect = this.#track.getBoundingClientRect()
      const slideRect = this.#slides[next].getBoundingClientRect()
      const left = this.#track.scrollLeft + (slideRect.left - trackRect.left)
      this.#track.scrollTo({
        left,
        behavior: animate ? 'smooth' : 'auto',
      })
      clearTimeout(this.#scrollSyncTimer)
      this.#scrollSyncTimer = setTimeout(() => {
        this.#scrollSyncing = false
      }, 500)
    }

    this.#startTimer()
  }

  #startTimer() {
    clearTimeout(this.#autoplayTimer)
    this.#autoplayTimer = undefined
    this.#autoplayRemaining = this.#autoplayMs
    this.classList.remove(PAUSED_CLASS)
    if (!this.#canAutoplay || this.#paused) {
      this.#clearFill()
      return
    }

    this.#autoplayStart = Date.now()
    this.#autoplayTimer = setTimeout(() => {
      this.#goTo(this.#index + 1, { animate: true })
    }, this.#autoplayRemaining)
    this.#restartFill()
  }

  #clearFill() {
    this.#items.forEach((item) => item.classList.remove(ITEM_FILLING_CLASS))
  }

  #restartFill() {
    this.#clearFill()
    const active = this.#items[this.#index]
    if (!active) return
    // Force reflow so re-adding the class replays the fill animation from 0.
    active.getBoundingClientRect()
    active.classList.add(ITEM_FILLING_CLASS)
  }

  #pause = () => {
    if (this.#paused) return
    this.#paused = true
    this.classList.add(PAUSED_CLASS)
    if (this.#autoplayTimer === undefined) return
    clearTimeout(this.#autoplayTimer)
    this.#autoplayTimer = undefined
    this.#autoplayRemaining = Math.max(
      0,
      this.#autoplayRemaining - (Date.now() - this.#autoplayStart)
    )
  }

  #resume = () => {
    if (!this.#paused) return
    this.#paused = false
    this.classList.remove(PAUSED_CLASS)
    if (!this.#canAutoplay) return

    const active = this.#items[this.#index]
    if (active && !active.classList.contains(ITEM_FILLING_CLASS)) {
      this.#restartFill()
    }

    this.#autoplayStart = Date.now()
    this.#autoplayTimer = setTimeout(() => {
      this.#goTo(this.#index + 1, { animate: true })
    }, this.#autoplayRemaining)
  }

  #onVisibility = () => {
    if (document.hidden) this.#pause()
    else this.#resume()
  }
}

if (!customElements.get('showcase-slideshow')) {
  customElements.define('showcase-slideshow', ShowcaseSlideshow)
}
